import { z } from 'zod';
import { FlowStatus, FooterType, Perspective, zPlanType, zPrintSize } from './general';
import { SectionView } from './confdefnComponents';
import { FabType, zClientDataApi, zConfigApi, zDataApi, HasActionButtonsMenu, defaultWorklistButtons, } from './confdefnView';
import { zBaseDefnsComponentProps } from './confdefnComponentProps';
// Confdefn tab and section structure validators
const Section = z.object({
    id: z.string(),
    name: z.string(),
    icon: z.string(),
    pathSlot: z.string(),
    inPerspectives: z.array(Perspective),
    tooltip: z.string().optional(),
    /** Available views to display under section in LeftNav */
    views: z.array(SectionView),
    defaultView: z.string().optional(),
    disabled: z.boolean().optional(),
    hidden: z.boolean().optional(),
});
export const LeftNavSection = Section;
export const DisabledTab = z.object({
    id: z.string(),
    name: z.string(),
    pathSlot: z.string(),
    disabled: z.literal(true),
    inPerspectives: z.array(Perspective),
});
const zContextButton = z.object({
    text: z.string().optional(),
    tooltip: z.string().optional(),
    inPerspectives: z.array(z.string()).optional().default([]),
});
const zEditStyleButton = z.object(Object.assign(Object.assign({}, zContextButton.shape), { path: z.string() }));
const zCartButton = z.object(Object.assign(Object.assign({}, zContextButton.shape), { iconSrc: z.string().optional().default('far fa-cart-arrow-down'), text: z.string().optional().default('Cart') }));
const zAgentButton = z.object(Object.assign(Object.assign({}, zContextButton.shape), { iconSrc: z.string().optional().default('fas fa-star-christmas'), text: z.string().optional().default('AI Assist'), dataApi: zDataApi.optional(), configApi: zConfigApi.optional() }));
// using `.and` instead because zDataApi is union type and `.merge` doesn't exist on that type
const zPlanBtn = zContextButton.and(zDataApi);
const zContextMfpModule = z.object({
    id: z.string(),
    pathSlot: z.string(),
    name: z.string(),
    siloId: z.string(),
    onlyIndirectMfpScoping: z.boolean().optional().default(false),
});
const zContextBookmarks = z.object({
    tabs: z.array(z.string()),
});
const zContextFilterConfig = z.object({
    disabled: z.boolean().optional().default(false),
    groups: z
        .object({
        blacklist: z.array(z.string()).default([]),
    })
        .default({
        blacklist: [],
    })
        .optional(),
});
const zQuickReconConfig = z.object(Object.assign(Object.assign({}, zBaseDefnsComponentProps('').shape), { configApi: zConfigApi }));
const zQuickReconButton = z.object(Object.assign(Object.assign({}, zContextButton.shape), zQuickReconConfig.shape));
const zContext = z.object({
    tabs: z.array(z.string()),
    undoBtn: zContextButton.optional(),
    editStyleBtn: zEditStyleButton.optional(),
    cartBtn: zCartButton.optional(),
    agentBtn: zAgentButton.optional(),
    planBtn: zPlanBtn.optional(),
    quickReconBtn: zQuickReconButton.optional(),
    mfpModule: zContextMfpModule.optional(),
    bookmarks: zContextBookmarks.optional(),
    worklistConfigButtons: z.array(HasActionButtonsMenu).optional().default(defaultWorklistButtons),
    /**
     * A better name for this would be `filterContextKey`
     *
     * Used to tell the filters if it should send in something other than the context key name */
    alternateContextKey: z.string().optional(),
    filters: zContextFilterConfig
        .default({
        disabled: false,
        groups: {
            blacklist: [],
        },
    })
        .optional(),
    planType: zPlanType.default('assortment').optional(),
});
const zAppContext = z.record(z.string(), zContext);
const getDefaultContext = () => ({
    tabs: [],
    worklistConfigButtons: defaultWorklistButtons,
    undoBtn: undefined,
    editStyleBtn: undefined,
    cartBtn: undefined,
    agentBtn: undefined,
    planBtn: undefined,
    quickReconBtn: undefined,
    mfpModule: undefined,
    bookmarks: undefined,
    alternateContextKey: undefined,
    filters: undefined,
});
const Tab = z.object({
    id: z.string(),
    /** Determines if tab is enabled in the UI */
    disabled: z.boolean().default(false).optional(),
    /** Selected section after tab loads */
    defaultSection: z.string(),
    /** Dash separated value used to generate the url of the selected tab  */
    pathSlot: z.string(),
    /** Sections to render in the LeftNav component */
    leftNavSections: z.array(LeftNavSection),
    /** Name of tab */
    name: z.string(),
    /** Perspectives of a tab */
    inPerspectives: z.array(Perspective),
    hidden: z.boolean().optional(),
    flowStatusOverride: FlowStatus.optional(),
});
export const ConfDefnTab = Tab;
export const isEnabledTab = (tab) => {
    return !tab.disabled;
};
const ScopeSelectorType = z.enum(['AssortmentScopeSelector', 'MfpScopeSelector']);
const ScopeWorkflow = z.enum(['in-season', 'pre-season']);
const HeaderbarType = z.enum(['AssortmentHeaderbar', 'MfpHeaderbar']);
export const DependentScopeType = z.enum(['MfpScope']);
const zDependentScopes = z.object({
    type: DependentScopeType,
});
export const zFabTypeBase = z.object({
    fabTooltip: z.string().optional(),
});
// Define individual fab types with specific properties
export const zCartFabType = z.object(Object.assign(Object.assign({}, zFabTypeBase.shape), { fabType: z.literal(FabType.cart) }));
export const zWorklistFabType = z.object(Object.assign(Object.assign({}, zFabTypeBase.shape), { fabType: z.literal(FabType.worklist) }));
export const zFabTypePlanning = z.object(Object.assign(Object.assign({}, zFabTypeBase.shape), { fabType: z.literal(FabType.planning), dataApi: zPlanBtn.optional(), defnId: z.string().optional() }));
export const zFabTypeButtonModal = z.object(Object.assign(Object.assign({}, zFabTypeBase.shape), { fabType: z.literal(FabType.buttonModal), configApi: zConfigApi.optional() }));
export const zPostTextModalFab = z.object(Object.assign(Object.assign({}, zFabTypeBase.shape), { fabType: z.literal(FabType.postTextModal), text: z.string(), dataApi: zClientDataApi.optional() }));
export const zFabTypePivot = z.object(Object.assign(Object.assign({}, zFabTypeBase.shape), { fabType: z.literal(FabType.pivot), fabIcon: z.string(), dataApi: zPlanBtn, toastError: z.boolean().optional().default(true), showRefreshFab: z.boolean().optional().default(false) }));
export const zFabTypeUnion = z.discriminatedUnion('fabType', [
    zCartFabType,
    zWorklistFabType,
    zFabTypePlanning,
    zFabTypeButtonModal,
    zPostTextModalFab,
    zFabTypePivot,
]);
const zFabTypeRecord = z.record(z.string(), zFabTypeUnion);
//#region FooterType
const zFooterTypeBase = z.object({
    tooltip: z.string().optional(),
});
const zNoneFooterType = z.object(Object.assign(Object.assign({}, zFooterTypeBase.shape), { footerType: z.literal(FooterType.none) }));
const zInsightsFooterType = z.object(Object.assign(Object.assign({}, zFooterTypeBase.shape), { footerType: z.literal(FooterType.insights), configApi: zConfigApi, dataApi: zDataApi }));
const zFooterTypeConfig = z.discriminatedUnion('footerType', [zNoneFooterType, zInsightsFooterType]);
export const zFooterTypeRecord = z.record(z.string(), zFooterTypeConfig);
//#endregion
export const AssortmentConfDefn = z.object({
    /** The type of project the configs are setup for */
    id: z.union([z.literal('Assortment'), z.literal('Configurator'), z.literal('_empty_')]),
    /** The selected tab after login and perspective selection */
    defaultTab: z.string(),
    version: z.number().optional(),
    schema: z.string().optional(),
    buttons: z.any().describe('This property is no longer used and can be removed'),
    canAddNewStyles: z.boolean().optional().default(true),
    showBookmarks: z.boolean().optional().default(false),
    /** Values shown for flowStatus and initial selections  */
    flowStatus: FlowStatus.default({ values: [], defaultSelection: [] }),
    fundedStatus: FlowStatus.default({ values: [], defaultSelection: [] }),
    /** Placeholder config for changing placeholder text, etc. */
    placeholder: z
        .object({
        text: z.string(),
    })
        .default({
        text: 'PLACEHOLDER',
    }),
    /** i.e. YTD, QTD, MTD, etc. */
    lookBackPeriods: z.array(z.string()).default([]),
    /** Available Perspectives */
    perspectives: z.array(Perspective),
    perspective: z
        .object({
        scopeConfig: z.object({
            type: ScopeSelectorType,
            workflow: ScopeWorkflow.optional(),
            dependentScopes: z.array(zDependentScopes).default([]).optional(),
        }),
        headerbar: z.object({
            type: HeaderbarType,
        }),
    })
        .default({
        scopeConfig: {
            type: 'AssortmentScopeSelector',
            dependentScopes: [],
        },
        headerbar: {
            type: 'AssortmentHeaderbar',
        },
    })
        .optional(),
    /** For Filters/Style Pane to map tabs to a predetermined application context */
    context: zAppContext.default({
        assortment: getDefaultContext(),
        hindsighting: getDefaultContext(),
        allocation: getDefaultContext(),
    }),
    /**
      Tabs to render in TopNavBar, containing Section and View configurations.
      Reporting Tab can actually be component itself so LeftNavSection is necessary in union
    */
    tabs: z.array(ConfDefnTab),
    /** Available print dimensions */
    printSizes: z.array(zPrintSize).default([
        { width: 8.5, height: 11 },
        { width: 11, height: 8.5 },
        { width: 17, height: 11 },
        { width: 16.54, height: 23.39, name: 'A2' },
        { width: 11.7, height: 16.5, name: 'A3' },
        { width: 8.3, height: 11.7, name: 'A4' },
    ]),
    /** For views like configuration */
    disableScope: z.boolean().optional().default(false),
    disableFilters: z.boolean().optional().default(false),
    fabTypes: zFabTypeRecord,
    footerTypes: zFooterTypeRecord.optional(),
    workflows: z
        .record(z.string(), z.object({
        clearSearch: z.boolean(),
    }))
        .optional()
        .default({}),
});
